import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadIcon from "@mui/icons-material/Download";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
  Box,
  Dialog,
  DialogContent,
  Grid2,
  IconButton,
  LinearProgress,
  Paper,
  useTheme,
} from "@mui/material";
import { observer } from "mobx-react";
import { useEffect, useRef, useState } from "react";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import InlineFormError from "../../../../../views/components/common/form/InlineFormError";
import {
  TrackingBulkUpdateJobStatus,
  TrackingBulkUpdateSkipped,
} from "../../../../../models/Shipments";
import { useStores } from "../../../use-stores";

const TEMPLATE_FILE_NAME = "Shipment_Tracking_Update_Template.csv";
const TEMPLATE_CSV = [
  "Shipment ID,Status (SHIPPED/DELIVERED),AWB,Tracking URL",
  "101,shipped,AWB123456,https://carrier.example/track/AWB123456",
  "102,delivered,AWB789012,https://carrier.example/track/AWB789012",
].join("\n");

const MAX_ROWS = 5000;
const MAX_FILE_BYTES = 5 * 1024 * 1024;
const JOB_POLL_MS = 3000;

// Same aliases the backend accepts.
const REQUIRED_COLUMNS: { label: string; aliases: string[] }[] = [
  { label: "Shipment ID", aliases: ["shipment id"] },
  { label: "Status", aliases: ["status"] },
  { label: "AWB", aliases: ["awb", "awb number"] },
  {
    label: "Tracking URL",
    aliases: ["tracking url", "tracking_url", "trackingurl"],
  },
];

const COLUMN_GUIDE = [
  { name: "Shipment ID", note: "Positive integer" },
  { name: "Status", note: "shipped or delivered (case-insensitive)" },
  { name: "AWB", note: "Non-empty tracking number" },
  { name: "Tracking URL", note: "Valid http or https URL" },
];

type UploadOutcome =
  | {
      kind: "applied";
      updatedCount: number;
      skipped: TrackingBulkUpdateSkipped[];
    }
  | { kind: "job"; jobId: number; totalCount: number };

interface TrackingCsvUploadPopupProps {
  open: boolean;
  onClose: () => void;
  /** Called once shipments were written, so the listing can reload. */
  onUpdated: () => void;
}

const validateCsvFile = async (file: File): Promise<string | null> => {
  if (!file.name.toLowerCase().endsWith(".csv")) {
    return "Please upload a CSV file.";
  }
  if (file.size > MAX_FILE_BYTES) {
    return "CSV must be 5MB or smaller.";
  }
  const lines = (await file.text())
    .split(/\r?\n/)
    .filter((line) => line.trim() !== "");
  if (lines.length < 2) {
    return "CSV has no shipment rows.";
  }
  if (lines.length - 1 > MAX_ROWS) {
    return `CSV has ${lines.length - 1} rows. The limit is ${MAX_ROWS}.`;
  }
  const headers = lines[0]
    .replace(/^﻿/, "")
    .split(",")
    .map((header) =>
      header.trim().replace(/^"|"$/g, "").toLowerCase().replace(/\s+/g, " "),
    );
  const missing = REQUIRED_COLUMNS.filter(
    (column) => !column.aliases.some((alias) => headers.includes(alias)),
  ).map((column) => column.label);
  if (missing.length > 0) {
    return `Missing column(s): ${missing.join(", ")}. Download the template for the expected format.`;
  }
  return null;
};

const TrackingCsvUploadPopup = observer(
  ({ open, onClose, onUpdated }: TrackingCsvUploadPopupProps) => {
    const theme = useTheme();
    const { shipmentStore } = useStores();
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const jobTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const onUpdatedRef = useRef(onUpdated);
    onUpdatedRef.current = onUpdated;

    const [csvFile, setCsvFile] = useState<File | null>(null);
    const [fileError, setFileError] = useState("");
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState("");
    const [outcome, setOutcome] = useState<UploadOutcome | null>(null);
    const [job, setJob] = useState<TrackingBulkUpdateJobStatus | null>(null);

    const isJobRunning = outcome?.kind === "job" && !job?.finished;

    useEffect(
      () => () => {
        if (jobTimerRef.current) {
          clearTimeout(jobTimerRef.current);
        }
      },
      [],
    );

    // A running background job keeps polling while closed; reopening shows its progress.
    useEffect(() => {
      if (!open || isJobRunning) return;
      setCsvFile(null);
      setFileError("");
      setUploadError("");
      setOutcome(null);
      setJob(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const pollJob = (jobId: number) => {
      jobTimerRef.current = setTimeout(async () => {
        jobTimerRef.current = null;
        try {
          const next = await shipmentStore.getTrackingBulkUpdateJob(jobId);
          setJob(next);
          if (!next.finished) {
            pollJob(jobId);
          } else if (next.success) {
            onUpdatedRef.current();
          }
        } catch (error: unknown) {
          setUploadError(
            error instanceof Error
              ? error.message
              : "Failed to fetch tracking update job.",
          );
        }
      }, JOB_POLL_MS);
    };

    const handleDownloadTemplate = () => {
      try {
        const blob = new Blob([TEMPLATE_CSV], {
          type: "text/csv;charset=utf-8",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = TEMPLATE_FILE_NAME;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
      } catch {
        setFileError("Failed to download the template. Please try again.");
      }
    };

    const handleFileChange = async (
      event: React.ChangeEvent<HTMLInputElement>,
    ) => {
      const file = event.target.files?.[0] || null;
      event.target.value = "";
      if (!file || uploading) return;
      setOutcome(null);
      setUploadError("");
      try {
        const error = await validateCsvFile(file);
        setCsvFile(error ? null : file);
        setFileError(error || "");
      } catch {
        setCsvFile(null);
        setFileError("Failed to read the CSV file.");
      }
    };

    const handleRemoveFile = () => {
      if (uploading) return;
      setCsvFile(null);
      setFileError("");
    };

    const handleUpload = async () => {
      if (!csvFile || uploading || isJobRunning) return;
      setUploading(true);
      setUploadError("");
      setOutcome(null);
      setJob(null);
      try {
        const res = await shipmentStore.uploadCsvForBulkEdit(csvFile);
        if (!res?.success) {
          setUploadError(
            shipmentStore.error || "Failed to update tracking details.",
          );
          return;
        }
        const result = res.result;
        if (result?.jobId != null) {
          setOutcome({
            kind: "job",
            jobId: result.jobId,
            totalCount: result.totalCount ?? 0,
          });
          pollJob(result.jobId);
        } else {
          setOutcome({
            kind: "applied",
            updatedCount: result?.updatedCount ?? 0,
            skipped: result?.skipped ?? [],
          });
          onUpdatedRef.current();
        }
        setCsvFile(null);
      } catch (error: unknown) {
        setUploadError(
          error instanceof Error
            ? error.message
            : "Failed to update tracking details.",
        );
      } finally {
        setUploading(false);
      }
    };

    const handleClose = () => {
      if (uploading) return;
      onClose();
    };

    const sectionLabel = (text: string) => (
      <CustomTypography variant="body2" fontWeight={600}>
        {text}
      </CustomTypography>
    );

    const renderOutcome = () => {
      if (!outcome) return null;

      if (outcome.kind === "applied") {
        const { updatedCount, skipped } = outcome;
        return (
          <Box sx={{ mt: 1.5 }}>
            <CustomTypography
              variant="body2"
              fontWeight={600}
              sx={{ color: theme.palette.customColors.successGreen }}
            >
              Updated {updatedCount} shipment{updatedCount === 1 ? "" : "s"}.
            </CustomTypography>
            {skipped.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <CustomTypography
                  variant="body3"
                  fontWeight={600}
                  sx={{ color: theme.palette.warning.main }}
                >
                  Skipped {skipped.length}:
                </CustomTypography>
                <Box
                  component="ul"
                  sx={{
                    m: 0,
                    mt: 0.5,
                    pl: 2.5,
                    maxHeight: 160,
                    overflow: "auto",
                  }}
                >
                  {skipped.map((row) => (
                    <li key={`${row.rowNumber}-${row.shipmentId}`}>
                      <CustomTypography variant="body3" color="text.secondary">
                        Row {row.rowNumber}, shipment {row.shipmentId}:{" "}
                        {row.reason}
                      </CustomTypography>
                    </li>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        );
      }

      const finished = Boolean(job?.finished);
      const failed = finished && !job?.success;
      const summary =
        job?.summary ||
        `Updating ${outcome.totalCount} shipments in the background.`;
      return (
        <Box sx={{ mt: 1.5 }}>
          <CustomTypography
            variant="body2"
            fontWeight={600}
            sx={{
              color: failed
                ? theme.palette.error.main
                : finished
                  ? theme.palette.customColors.successGreen
                  : undefined,
            }}
          >
            {failed
              ? "Tracking update failed."
              : finished
                ? "Tracking update finished."
                : `Job ${outcome.jobId} running…`}
          </CustomTypography>
          {!finished && (
            <LinearProgress
              variant={job ? "determinate" : "indeterminate"}
              value={job?.progress ?? 0}
              sx={{ mt: 1, borderRadius: 4, height: 6 }}
            />
          )}
          <CustomTypography
            variant="body3"
            color="text.secondary"
            sx={{ mt: 0.75, display: "block" }}
          >
            {summary}
          </CustomTypography>
          {!finished && (
            <CustomTypography
              variant="body3"
              color="text.secondary"
              sx={{ mt: 0.5, display: "block" }}
            >
              You can close this window. The listing reloads when the job
              finishes.
            </CustomTypography>
          )}
        </Box>
      );
    };

    return (
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: theme.palette.customColors.backdrop,
              backdropFilter: "blur(4px)",
            },
          },
          paper: {
            sx: {
              borderRadius: "16px",
              overflow: "hidden",
              boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
              background: `linear-gradient(135deg, ${theme.palette.customColors.white} 0%, ${theme.palette.customColors.lightGreyColor} 100%)`,
              position: "relative",
            },
          },
        }}
      >
        <Box
          sx={{
            position: "relative",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "4px",
              background: `linear-gradient(90deg, ${theme.palette.customColors.blueGradientStart} 0%, ${theme.palette.customColors.blueGradientEnd} 50%, ${theme.palette.customColors.blueGradientStart} 100%)`,
            },
          }}
        >
          <Box
            sx={{
              px: 3,
              py: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              borderBottom: `1px solid ${theme.palette.customColors.greyBorder}`,
            }}
          >
            <Box minWidth={0}>
              <CustomTypography variant="heading4" fontWeight={700}>
                Add Tracking Details
              </CustomTypography>
              <CustomTypography variant="body3" color="text.secondary">
                Bulk update AWB, tracking link and status from a CSV.
              </CustomTypography>
            </Box>
            <IconButton
              onClick={handleClose}
              disabled={uploading}
              aria-label="Close"
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <DialogContent sx={{ p: 3 }}>
            <Grid2 container spacing={2}>
              <Grid2 size={12}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      gap: 1.5,
                      flexWrap: "wrap",
                    }}
                  >
                    <Box minWidth={0}>
                      {sectionLabel("CSV TEMPLATE")}
                      <CustomTypography variant="body3" color="text.secondary">
                        Max {MAX_ROWS} rows, one row per shipment.
                      </CustomTypography>
                    </Box>
                    <AppButton
                      label="Download Template"
                      variant="secondary"
                      startIcon={<DownloadIcon />}
                      onClick={handleDownloadTemplate}
                      disabled={uploading}
                    />
                  </Box>
                  <Grid2 container spacing={1} sx={{ mt: 1.5 }}>
                    {COLUMN_GUIDE.map((column) => (
                      <Grid2 key={column.name} size={{ xs: 12, sm: 6 }}>
                        <CustomTypography variant="body3" fontWeight={600}>
                          {column.name}
                        </CustomTypography>
                        <CustomTypography
                          variant="body3"
                          color="text.secondary"
                          sx={{ display: "block" }}
                        >
                          {column.note}
                        </CustomTypography>
                      </Grid2>
                    ))}
                  </Grid2>
                  <CustomTypography
                    variant="body3"
                    color="text.secondary"
                    sx={{ mt: 1.5, display: "block" }}
                  >
                    Delivered needs the shipment to be Shipped or later. Send
                    the current status to change only the AWB / link. Returned
                    shipments are re-shipped with the new tracking details. If
                    any row fails validation, nothing is updated.
                  </CustomTypography>
                </Paper>
              </Grid2>

              <Grid2 size={12}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 3 }}>
                  {sectionLabel("CSV FILE")}
                  <CustomTypography variant="body3" color="text.secondary">
                    Upload the filled tracking template
                  </CustomTypography>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,text/csv"
                    hidden
                    onChange={(event) => {
                      void handleFileChange(event);
                    }}
                  />
                  <Box
                    sx={{
                      mt: 1.5,
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      flexWrap: "wrap",
                    }}
                  >
                    <AppButton
                      label={csvFile ? "Replace CSV" : "Choose CSV"}
                      variant="grayscale"
                      startIcon={<UploadFileIcon />}
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading || isJobRunning}
                    />
                    {csvFile && (
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={0.5}
                        minWidth={0}
                      >
                        <CustomTypography
                          variant="body2"
                          fontWeight={600}
                          sx={{ wordBreak: "break-all" }}
                        >
                          {csvFile.name}
                        </CustomTypography>
                        <IconButton
                          size="small"
                          onClick={handleRemoveFile}
                          disabled={uploading}
                          aria-label="Remove CSV file"
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                  <InlineFormError message={fileError} sx={{ mt: 1.5 }} />
                  <InlineFormError
                    message={uploadError}
                    sx={{ mt: 1.5, whiteSpace: "pre-line" }}
                  />
                  {renderOutcome()}
                </Paper>
              </Grid2>

              <Grid2
                size={12}
                display="flex"
                justifyContent="flex-end"
                gap={1.5}
                sx={{ pt: 1 }}
              >
                <AppButton
                  label={outcome ? "Done" : "Cancel"}
                  onClick={handleClose}
                  variant="grayscale"
                  disabled={uploading}
                />
                <AppButton
                  label={uploading ? "Uploading…" : "Upload CSV"}
                  onClick={() => {
                    void handleUpload();
                  }}
                  variant="primary"
                  disabled={uploading || isJobRunning || !csvFile}
                />
              </Grid2>
            </Grid2>
          </DialogContent>
        </Box>
      </Dialog>
    );
  },
);

export default TrackingCsvUploadPopup;
